import { memo, useCallback } from "react";

import { WAREHOUSE_WEEK_DAYS } from "services/api/fleet/warehouses/consts";
import { WarehouseWorkingDay } from "services/api/fleet/warehouses/types";
import { Button } from "ui/components/Button/Button";

import WarehouseWorkingDayRow from "./components/WarehouseWorkingDayRow";
import WarehouseWorkingHoursPresetButton from "./components/WarehouseWorkingHoursPresetButton";
import { WAREHOUSE_WORKDAYS, WAREHOUSE_WORKING_HOURS_PRESETS } from "./consts";
import { WarehouseWorkingHoursPreset, WarehouseWorkingHoursStepProps } from "./types";

/**
 * Korak radnog vremena: predložak postavi cijeli tjedan jednim klikom, a svaki dan se zatim može
 * zasebno otvoriti ili zatvoriti i dobiti svoje vrijeme.
 */
const WarehouseWorkingHoursStep: React.FC<WarehouseWorkingHoursStepProps> = ({ formValues, setFormValues }) => {
  const workingHours = formValues.workingHours;

  const setWorkingHours = useCallback(
    (update: (days: WarehouseWorkingDay[]) => WarehouseWorkingDay[]) => {
      setFormValues((prev) => ({ ...prev, workingHours: update(prev.workingHours) }));
    },
    [setFormValues]
  );

  const handleDayChange = useCallback(
    (changedDay: WarehouseWorkingDay) => {
      setWorkingHours((days) => days.map((day) => (day.day === changedDay.day ? changedDay : day)));
    },
    [setWorkingHours]
  );

  const handlePresetClick = useCallback(
    (preset: WarehouseWorkingHoursPreset) => {
      setWorkingHours((days) =>
        days.map((day) => ({
          ...day,
          isOpen: preset.openDays.includes(day.day),
          from: preset.from,
          to: preset.to,
        }))
      );
    },
    [setWorkingHours]
  );

  // Ponedjeljak je obično uzor tjedna: njegov raspored ide na ostale radne dane.
  const handleCopyMondayClick = useCallback(() => {
    setWorkingHours((days) => {
      const monday = days.find((day) => day.day === 1);
      if (!monday) return days;

      return days.map((day) =>
        WAREHOUSE_WORKDAYS.includes(day.day) ? { ...day, isOpen: monday.isOpen, from: monday.from, to: monday.to } : day
      );
    });
  }, [setWorkingHours]);

  return (
    <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Radno vrijeme</h1>
        <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
          Odaberite predložak ili za svaki dan označite radi li skladište i od kada do kada.
          Nakon unosa možete nastaviti na sljedeći korak.
        </p>

        <div className="px-3">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {WAREHOUSE_WORKING_HOURS_PRESETS.map((preset) => (
              <WarehouseWorkingHoursPresetButton
                key={preset.key}
                preset={preset}
                onClick={handlePresetClick}
              />
            ))}
            <div className="flex-1" />
            <Button variant="text" size="small" onClick={handleCopyMondayClick}>
              Kopiraj ponedjeljak na radne dane
            </Button>
          </div>

          <div className="border border-slate-200 rounded-sm overflow-hidden">
            {WAREHOUSE_WEEK_DAYS.map(({ day, label }) => {
              const workingDay = workingHours.find((workingDay) => workingDay.day === day);
              if (!workingDay) return null;

              return (
                <WarehouseWorkingDayRow
                  key={day}
                  label={label}
                  workingDay={workingDay}
                  onChange={handleDayChange}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(WarehouseWorkingHoursStep);
